import { CardFieldHandle, type CardFieldMountOptions } from './card-field';
import { MentaError } from './errors';
import { FieldGroup, type FieldGroupMountOptions } from './field-group';
import type { InstallmentsSource } from './installments';
import { InstallmentsFeed } from './installments-feed';
import { CardFieldSession } from './session';
import type { SiteStatus, SiteStatusGate } from './site-status';
import type { FieldStyleByState, IframeTheme } from './transport/iframe';
import type { MessageRouter } from './transport/message-router';
import type { FieldName } from '../protocol';

/** Constructor-level `locale`/`theme`/`style` defaults (Engram
 * odd/sdk-options/tasks, items 1/5), applied to every field created via
 * `create()` unless overridden by that call's own `options`. */
export interface FieldsControllerDefaults {
  locale?: string;
  theme?: IframeTheme;
  style?: FieldStyleByState;
}

/** Nonce generator for a group's shared `groupId` (design D2). Falls back
 * to a `Math.random`-based id when `crypto.randomUUID` is unavailable
 * (older browsers / non-secure-context test environments). */
function generateGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `grp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/**
 * `FieldsController` — the public entry point for the per-input split API,
 * exposed as `menta.fields`. `create()` never mounts a field iframe by
 * itself: the merchant still calls `.mount(el)` on the returned
 * `CardFieldHandle` (spec: "Merchant mounts three fields in custom
 * layout"). The 4th, headless coordinator iframe mounts lazily on the
 * FIRST `create()` call in this controller's group and never again for
 * later `create()` calls in the same group (spec: "Coordinator auto-mounts
 * on first field creation").
 *
 * `menta.fields.create()` is the ONLY public per-field API surface (no
 * legacy `mount()` wrapper — see engram
 * `sdd/split-secure-fields-per-input/decision-no-legacy`).
 */
export class FieldsController {
  private readonly groupId = generateGroupId();
  private readonly group: FieldGroup;
  /** Every handle returned by `create()`, so `destroy()` can unmount ALL of
   * them (Engram odd/sdk-options/tasks, item 4) — the merchant is not
   * required to have called `unmount()` on each one itself first. */
  private readonly handles: CardFieldHandle[] = [];
  /** Set once by `destroy()` (Engram odd/sdk-options/tasks, item 4);
   * `create()`/`createCardToken()` throw afterward instead of silently
   * operating on a torn-down group. */
  private destroyed = false;

  constructor(
    private readonly origin: string,
    private readonly router: MessageRouter,
    private readonly siteKey: string,
    private readonly defaults: FieldsControllerDefaults = {},
    /** Advisory site-status pre-check (absent = mount exactly as before). */
    private readonly siteStatus?: SiteStatusGate,
    /** api-checkouts installments client (absent = no installments events). */
    private readonly installments?: InstallmentsSource,
  ) {
    this.group = new FieldGroup(this.origin, this.router, this.groupId, this.siteKey);
  }

  /** Merges constructor-level `locale`/`theme`/`style` defaults under this
   * call's own `options` — a per-field value of the same name always wins
   * (Engram odd/sdk-options/tasks, items 1/5). `theme`/`style` merge
   * key-by-key (constructor value as the base, per-field keys overriding);
   * `style` does so within each state (see `mergeStyle`), everything else is
   * a simple override. */
  create(name: FieldName, options: CardFieldMountOptions): CardFieldHandle {
    if (this.destroyed) {
      throw new MentaError('destroyed', 'menta.destroy() was already called');
    }
    const mergedOptions: CardFieldMountOptions = {
      ...options,
      locale: options.locale ?? this.defaults.locale,
      theme:
        this.defaults.theme || options.theme
          ? { ...this.defaults.theme, ...options.theme }
          : undefined,
      style: mergeStyle(this.defaults.style, options.style),
    };
    this.mountCoordinatorWhenAllowed(mergedOptions);
    const session = new CardFieldSession(this.origin, this.router, this.siteKey);
    // Only the PAN carries a BIN, so only its handle gets an installments feed.
    const feed =
      name === 'pan' && this.installments
        ? new InstallmentsFeed(this.group, mergedOptions.token, this.installments, this.siteStatus)
        : undefined;
    const handle = new CardFieldHandle(name, session, this.group, this.groupId, mergedOptions, this.siteStatus, feed);
    this.handles.push(handle);
    return handle;
  }

  createCardToken(): Promise<{ cardReferenceId: string }> {
    if (this.destroyed) {
      throw new MentaError('destroyed', 'menta.destroy() was already called');
    }
    if (this.siteStatus?.peek()?.status === 'not_authorized') {
      return Promise.reject(
        new MentaError('site_not_authorized', 'the site key or this page origin is not authorized'),
      );
    }
    return this.group.createCardToken();
  }

  /** The coordinator iframe is also gated by the advisory pre-check: with a
   * pending outcome nothing is inserted (and no handshake timer starts)
   * until it settles; `not_authorized` never mounts it. */
  private mountCoordinatorWhenAllowed(options: FieldGroupMountOptions): void {
    const gate = this.siteStatus;
    const apply = (outcome: SiteStatus): void => {
      if (this.destroyed || outcome.status === 'not_authorized') return;
      if (outcome.status === 'unavailable') gate?.warnUnavailableOnce();
      this.group.ensureMounted(options);
    };
    const current = gate?.peek();
    if (!gate) {
      this.group.ensureMounted(options);
    } else if (current) {
      apply(current);
    } else {
      void gate.whenSettled().then(apply);
    }
  }

  /** Unmounts the coordinator iframe (which, in turn, was the only thing
   * keeping this group alive) — individual field iframes are unmounted by
   * their own `CardFieldHandle.unmount()`, which the merchant is expected
   * to have called, or which simply go away with their container. Marks
   * this controller destroyed so any later `create()`/`createCardToken()`
   * call fails fast instead of operating on a torn-down group. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const handle of this.handles) {
      handle.unmount();
    }
    this.handles.length = 0;
    this.group.unmount();
  }
}

const STYLE_STATES = ['base', 'focus', 'invalid', 'complete', 'placeholder'] as const;

/** Merges `style` one level deeper than `theme`: per state, the constructor
 * properties are the base and the per-field properties override them, so a
 * per-field `base: { fontSize }` keeps a constructor `base: { color }`. */
function mergeStyle(
  defaults: FieldStyleByState | undefined,
  overrides: FieldStyleByState | undefined,
): FieldStyleByState | undefined {
  if (!defaults && !overrides) return undefined;
  const merged: FieldStyleByState = {};
  for (const state of STYLE_STATES) {
    if (defaults?.[state] || overrides?.[state]) {
      merged[state] = { ...defaults?.[state], ...overrides?.[state] };
    }
  }
  return merged;
}
